/**
 * API Client for DAC Backend
 * Base configuration and fetch utilities
 */

// Runtime configuration interface (loaded from /config.js)
interface AppConfig {
  apiBaseUrl: string;
  apiKey: string;
}

// Extend Window interface for runtime config
declare global {
  interface Window {
    __APP_CONFIG__?: AppConfig;
  }
}

// Get runtime configuration from window object (client-side only)
const getRuntimeConfig = (): AppConfig | null => {
  if (typeof window !== 'undefined' && window.__APP_CONFIG__) {
    return window.__APP_CONFIG__;
  }
  return null;
};

// Environment-based API URL configuration
const getApiBaseUrl = (): string => {
  // 1. Runtime config from /config.js (Cloudflare Pages can override this)
  const runtimeConfig = getRuntimeConfig();
  if (runtimeConfig?.apiBaseUrl) {
    return runtimeConfig.apiBaseUrl;
  }

  // 2. Explicit override via environment variable (build-time)
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // 3. Development mode: use local backend
  if (import.meta.env.DEV) {
    return 'http://localhost:8787';
  }

  // 4. Production mode: use production backend
  return 'https://dac-backend.yanggf.workers.dev';
};

const getApiKey = (): string => {
  const runtimeConfig = getRuntimeConfig();
  if (runtimeConfig?.apiKey) {
    return runtimeConfig.apiKey;
  }
  return import.meta.env.VITE_API_KEY || 'yanggf';
};

export const API_BASE_URL = getApiBaseUrl();

export class APIError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: unknown
  ) {
    super(message);
    this.name = 'APIError';
  }
}

/**
 * Standard backend response wrapper
 */
export interface BackendResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: string;
  cached?: boolean;
  cacheStatus?: string;
  cachedAt?: string;
  stale?: boolean;
}

export async function apiRequest<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${getApiBaseUrl()}${endpoint}`;

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': getApiKey(),
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        errorData
      );
    }

    const json = await response.json();
    if (import.meta.env.DEV) {
      console.log('API Response:', { url, status: response.status, json });
    }
    
    // Check if this is a wrapped response {success, data, ...}
    if (json.success !== undefined && json.data !== undefined) {
      // Unwrap and attach metadata to the data object
      const result = json.data;
      if (json.cached !== undefined) {
        (result as any).cached = json.cached;
      }
      if (json.cacheStatus !== undefined) {
        (result as any).cacheStatus = json.cacheStatus;
      }
      if (json.cachedAt !== undefined) {
        (result as any).cachedAt = json.cachedAt;
      }
      if (json.stale !== undefined) {
        (result as any).stale = json.stale;
      }
      return result as T;
    }
    
    // Return raw response for non-wrapped responses
    return json as T;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error instanceof Error ? error.message : 'Unknown error occurred',
      0
    );
  }
}
